import { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";

import {
  useResolvedRateSchedules,
  useUpdateRate,
  useUpdateProposalRate,
} from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import {
  currentSchedules,
  getRenewalColumns,
  slotKeyOf,
} from "../../../utils/rateSets";
import { buildRateFieldPath } from "../../../utils/rateFieldPath";

import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import { RenewalHeaderRow, RenewalRateCell } from "./RenewalRateCell";
import {
  FONT_SIZE,
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  inputSx,
  gridRowSx,
  gridCellSx,
  truncatedLabelCellSx,
  getUnitLabel,
} from "./constants";

import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { BenefitPlan } from "../../../types/models";
import type { Rate, RateSchedule } from "../../../types";

export interface AdndRateRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  /** Coverage type for LOC-specific behavior */
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
}

/** Coverage tier codes for dependent rates */
const DEPENDENT_TIERS = [
  "employeeDependent",
  "spouseOnly",
  "childOnly",
] as const;
type DependentTierCode = (typeof DEPENDENT_TIERS)[number];

/** Check if a rate has a dependent coverage tier code */
const isDependentRate = (rate: Rate): boolean =>
  DEPENDENT_TIERS.includes(rate.coverageTierCode as DependentTierCode);

/**
 * Get the coverage tier display label for AD&D rates.
 */
const getAdndRateLabel = (rate: Rate): string => {
  switch (rate.coverageTierCode) {
    case "spouseOnly":
      return "Spouse AD&D";
    case "childOnly":
      return "Child AD&D";
    case "employeeDependent":
      return "Dependent AD&D";
    default:
      return "Employee AD&D";
  }
};

/** Get the dependent tier display label */
const getDependentTierLabel = (rate: Rate): string => {
  switch (rate.coverageTierCode) {
    case "spouseOnly":
      return "Spouse";
    case "childOnly":
      return "Child";
    case "employeeDependent":
    default:
      return "Dependent";
  }
};

/**
 * Derive whether AD&D dependent rates are combined or separate.
 * employeeDependent tier → combined; spouseOnly/childOnly → separate.
 */
const deriveDependentRateType = (rates: Rate[]): "combined" | "separate" =>
  rates.some((r) => r.coverageTierCode === "employeeDependent")
    ? "combined"
    : "separate";

// CSS Grid: Label + Value columns. With renewal a third column is appended and the table
// scrolls within the pinned benefit-plan cell.
const gridSx = (showRenewal: boolean): Record<string, unknown> =>
  showRenewal
    ? {
        display: "grid",
        gridTemplateColumns: "minmax(90px, 1fr) repeat(2, minmax(80px, 1fr))",
        width: "100%",
        overflowX: "auto",
      }
    : { display: "grid", gridTemplateColumns: "3fr 2fr", width: "100%" };

/**
 * Renders AD&D rate rows for a life plan.
 * AD&D rates are always flat (no age bands, gender, tobacco, or tiers).
 * Displays a labeled section with one row per rate (Employee, Dependent/Spouse/Child).
 */
export const AdndRateRenderer: FC<AdndRateRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
}) => {
  const { data: rateSchedules, isLoading } = useResolvedRateSchedules(
    plan,
    axiosInstance,
  );

  // Collect all AD&D schedules. Some carriers (Unum) send one schedule per
  // coverage tier (employee, spouseOnly, childOnly) instead of mixing all
  // tier rates into a single schedule.
  const adndSchedules = useMemo(
    () =>
      rateSchedules?.filter((s) => s.rateCoverageIndicator === "adnd") ?? [],
    [rateSchedules],
  );

  // Editable rows come from the current-category AD&D schedules only; renewal/revised rates
  // live in their own categories and surface in the appended renewal column.
  const currentAdndSchedules = useMemo(
    () => currentSchedules(adndSchedules),
    [adndSchedules],
  );

  // Renewal display for the AD&D section, derived from its own (adnd) schedules.
  const renewal = useMemo(
    () => getRenewalColumns(adndSchedules),
    [adndSchedules],
  );
  const showRenewal = renewal.showRenewal;

  // Flatten rates across all current AD&D schedules into one list.
  const allAdndRates = useMemo(
    () => currentAdndSchedules.flatMap((s) => s.rates ?? []),
    [currentAdndSchedules],
  );

  // Map each rate id to its parent schedule, for mutation routing and for the
  // review field path. Carriers may split AD&D across several schedules, so the
  // rate alone does not say which one it came from.
  const scheduleByRateId = useMemo(
    () =>
      new Map(
        currentAdndSchedules.flatMap((s) =>
          s.id == null
            ? []
            : (s.rates ?? [])
                .filter((r) => r.id != null)
                .map((r): [string, RateSchedule] => [r.id!, s]),
        ),
      ),
    [currentAdndSchedules],
  );

  // Separate employee rates from dependent-tier rates.
  const employeeRates = useMemo(
    () => allAdndRates.filter((r) => !isDependentRate(r)),
    [allAdndRates],
  );

  const dependentRates = useMemo(
    () => allAdndRates.filter(isDependentRate),
    [allAdndRates],
  );

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  const rateFieldPathOf = (rate: Rate): string | undefined => {
    const schedule =
      scheduleByRateId.get(rate.id ?? "") ?? currentAdndSchedules[0];
    return schedule && buildRateFieldPath(schedule, rate, "rate");
  };

  const handleRateCommit = (rate: Rate) => (value: string) => {
    const scheduleId =
      scheduleByRateId.get(rate.id ?? "")?.id ?? currentAdndSchedules[0]?.id;
    if (!axiosInstance || !scheduleId || !rate.id) return;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || numericValue === rate.rate) return;
    rateMutation.mutate(
      {
        scheduleId,
        rateId: rate.id,
        benefitPlanId: plan.id,
        rate: { rate: numericValue },
      },
      {
        onSuccess: () => onRateSaved(),
      },
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1,
        }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (adndSchedules.length === 0 || allAdndRates.length === 0) {
    return null;
  }

  const unitLabel = getUnitLabel(
    employeeRates[0]?.unit ?? allAdndRates[0]?.unit,
  );
  const hasDependents = dependentRates.length > 0;
  const dependentRateType = hasDependents
    ? deriveDependentRateType(dependentRates)
    : "combined";

  return (
    <Box sx={{ width: "100%", mt: 0.5 }}>
      {/* AD&D Employee Section Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "32px",
          backgroundColor: AG_HEADER_BG_COLOR,
          borderBottom: AG_BORDER,
          borderTop: AG_BORDER,
          px: 1,
        }}
      >
        <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>AD&D</Box>
      </Box>

      {/* Employee AD&D Rate Rows */}
      {employeeRates.length > 0 && (
        <Box sx={gridSx(showRenewal)}>
          <RenewalHeaderRow show={showRenewal} label={renewal.columnLabel} />
          {employeeRates.map((rate, index) => {
            const label = getAdndRateLabel(rate);
            const isLastRow =
              index === employeeRates.length - 1 && !hasDependents;

            return (
              <Box key={rate.id} sx={gridRowSx}>
                <Box
                  title={`${label} ${unitLabel}`}
                  sx={{
                    ...truncatedLabelCellSx,
                    ...(isLastRow && { borderBottom: "none" }),
                  }}
                >
                  {label} {unitLabel}
                </Box>
                <RateReviewCell
                  planId={plan.id}
                  fieldPath={rateFieldPathOf(rate)}
                  sx={{
                    ...gridCellSx,
                    borderRight: showRenewal ? AG_BORDER : "none",
                    ...(isLastRow && { borderBottom: "none" }),
                  }}
                >
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      rate.rate,
                      employeeRates[0]?.unit,
                      coverageType,
                    )}
                    onCommit={handleRateCommit(rate)}
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`adnd-rate-${plan.id}`}
                    cellIndex={0}
                    columnsInRow={1}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${label} rate` }}
                  />
                </RateReviewCell>
                {showRenewal && (
                  <RenewalRateCell
                    slotKey={slotKeyOf(rate)}
                    renewal={renewal}
                    rowLabel={label}
                    isLastRow={isLastRow}
                    coverageType={coverageType}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* AD&D Dependent Rates Sub-Section */}
      {hasDependents && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "32px",
              backgroundColor: AG_HEADER_BG_COLOR,
              borderBottom: AG_BORDER,
              borderTop: AG_BORDER,
              px: 1,
            }}
          >
            <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
              AD&D Dependents / {dependentRateType}
            </Box>
          </Box>

          <Box sx={gridSx(showRenewal)}>
            <RenewalHeaderRow show={showRenewal} label={renewal.columnLabel} />
            {dependentRates.map((rate, index) => {
              const tier = getDependentTierLabel(rate);
              const isLastRow = index === dependentRates.length - 1;

              return (
                <Box key={rate.id} sx={gridRowSx}>
                  <Box
                    title={`${tier} ${unitLabel}`}
                    sx={{
                      ...truncatedLabelCellSx,
                      ...(isLastRow && { borderBottom: "none" }),
                    }}
                  >
                    {tier} {unitLabel}
                  </Box>
                  <RateReviewCell
                    planId={plan.id}
                    fieldPath={rateFieldPathOf(rate)}
                    sx={{
                      ...gridCellSx,
                      borderRight: showRenewal ? AG_BORDER : "none",
                      ...(isLastRow && { borderBottom: "none" }),
                    }}
                  >
                    <DebouncedNumericInput
                      serverValue={formatRateValue(
                        rate.rate,
                        dependentRates[0]?.unit,
                        coverageType,
                      )}
                      onCommit={handleRateCommit(rate)}
                      allowDecimals
                      isCurrency
                      sx={inputSx}
                      gridId={`adnd-dep-rate-${plan.id}`}
                      cellIndex={0}
                      columnsInRow={1}
                      readOnly={readOnly}
                      inputProps={{
                        "aria-label": `${tier} AD&D ${unitLabel}`,
                      }}
                    />
                  </RateReviewCell>
                  {showRenewal && (
                    <RenewalRateCell
                      slotKey={slotKeyOf(rate)}
                      renewal={renewal}
                      rowLabel={`${tier} AD&D`}
                      isLastRow={isLastRow}
                      coverageType={coverageType}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};
